import type * as React from 'react'
import { useCallback, useState } from 'react'

import { getPublishUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import { Service } from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'
import { Button, CoachMark, SimpleSpinner } from '@buffer-mono/popcorn'

import type { CreateCallback } from '~publish/legacy/composer/interfaces/types'

import AppActionCreators from '../../action-creators/AppActionCreators'
import AppStore from '../../stores/AppStore'

interface ThreadsLocationRefreshCoachmarkProps {
  open: boolean
  onDismiss: () => void
  channelIds: string[]
  saveAsDraft: CreateCallback
  children: React.ReactNode
}

export const ThreadsLocationRefreshCoachmark: React.FC<
  ThreadsLocationRefreshCoachmarkProps
> = ({ open, onDismiss, channelIds, saveAsDraft, children }) => {
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false)

  const handleRefreshChannel = useCallback((): void => {
    const orgData = AppStore.getOrganizationsData()
    const channelId = channelIds[0] // Use the first selected Threads channel

    if (!channelId) {
      return
    }

    const isNewPostMode = AppStore.getIsNewPostMode()

    // For new posts, try to use appshell actions if available
    if (
      isNewPostMode &&
      window.appshell?.actions &&
      Object.keys(window.appshell.actions).length > 0
    ) {
      setIsSavingAsDraft(true)

      saveAsDraft()
        .then(({ successfulResponses }) => {
          const updateId = successfulResponses[0]?.updates.find(
            (update) => update.profile_id === channelId,
          )?.id

          return window.appshell?.actions?.connectChannel({
            selectedService: Service.threads,
            isRefreshingConnection: true,
            skipCelebrationModal: true,
            cta: `account-channels-channelRefreshDropdown-threads-1`,
            selectedRefreshChannelId: channelId,
            role: orgData.selected.isAdmin ? 'admin' : 'member',
            destinationUrl: getPublishUrl(
              `channels/${channelId}${
                updateId && updateId !== 'composer'
                  ? `/posts/${updateId}/edit`
                  : '?tab=drafts'
              }`,
            ),
          })
        })
        .catch(() => {
          setIsSavingAsDraft(false)
        })
    } else {
      // For edited posts or when appshell is not available, save and redirect to settings
      setIsSavingAsDraft(true)

      const savePromise = isNewPostMode
        ? saveAsDraft()
        : AppActionCreators.saveDrafts('SAVE', {
            shouldSkipEmptyTextAlert: true,
            hasCampaignsToTagsFeature: true,
          })

      savePromise
        // eslint-disable-next-line promise/always-return
        .then((): void => {
          // Redirect to channels settings page where user can refresh manually
          window.location.href = getPublishUrl('settings/channels')
        })
        .catch(() => {
          setIsSavingAsDraft(false)
        })
    }
  }, [channelIds, saveAsDraft])

  return (
    <CoachMark open={open} onDismiss={onDismiss}>
      <CoachMark.Overlay style={{ display: 'inline' }}>
        {children}
      </CoachMark.Overlay>

      <CoachMark.Content>
        <CoachMark.Title>{`Let's setup Threads locations!`}</CoachMark.Title>
        <CoachMark.Description>
          To get everything ready for adding locations, a quick channel refresh
          is needed.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="tertiary">Not Now</Button>
          </CoachMark.Dismiss>
          <Button
            variant="primary"
            disabled={isSavingAsDraft}
            onClick={handleRefreshChannel}
          >
            {isSavingAsDraft ? (
              <>
                <SimpleSpinner /> Saving as Draft
              </>
            ) : (
              'Refresh Channel'
            )}
          </Button>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
